import * as THREE from 'three';
import { GROUND, AUTOPILOT, ENV, CAMERA } from '../core/SimConfig.js';
import { smoothstep, clamp, DEG2RAD } from '../utils.js';

const _targetDir = new THREE.Vector3();
const _fwdXZ = new THREE.Vector3();
const _targetXZ = new THREE.Vector3();
const _cross = new THREE.Vector3();
const _cross2 = new THREE.Vector3();
const _perpTarget = new THREE.Vector3();
const _worldUpPerp = new THREE.Vector3();
const _desiredUp = new THREE.Vector3();
const _omegaBody = new THREE.Vector3();
const _invQuat = new THREE.Quaternion();

/**
 * The Basic scheme's bank-to-turn autopilot ("War Thunder" mouse aim): turns a
 * world-space look direction into elevator/aileron/rudder commands. Writes its
 * commands to `out` rather than to the aircraft, so the controller can blend a
 * keyboard takeover over individual axes.
 */
export default class LookToSteerPilot {
  constructor(aircraft) {
    this.aircraft = aircraft;
    this.out = { elevator: 0, ailerons: 0, rudder: 0 };

    this.maxBankAngleLow = AUTOPILOT.maxBankAngleLow;
    this.maxBankAngleHigh = AUTOPILOT.maxBankAngleHigh;
    this.bankSpeedLow = AUTOPILOT.bankSpeedLow;
    this.bankSpeedHigh = AUTOPILOT.bankSpeedHigh;
    this.rollGain = AUTOPILOT.rollGain;
    this.rollDamping = AUTOPILOT.rollDamping;
    this.pitchGain = AUTOPILOT.pitchGain;
    this.pitchDamping = AUTOPILOT.pitchDamping;
    this.zoomRollGain = AUTOPILOT.zoomRollGain;
    this.zoomRollDamping = AUTOPILOT.zoomRollDamping;
    this.zoomPitchGain = AUTOPILOT.zoomPitchGain;
    this.zoomPitchDamping = AUTOPILOT.zoomPitchDamping;
    this.zoomYawGain = AUTOPILOT.zoomYawGain;
    this.zoomYawDamping = AUTOPILOT.zoomYawDamping;
    this._zoomBoost = 0;       // 0..1 smoothed: 1 when RMB-zoomed in cockpit
    this.rudderGain = AUTOPILOT.rudderGain;
    this.rudderGainGround = AUTOPILOT.rudderGainGround;
    this.aoaProtectionStart = AUTOPILOT.aoaProtectionStart;
    this.negativeGLimit = AUTOPILOT.negativeGLimit;
    this.wingsLevelThreshold = AUTOPILOT.wingsLevelThreshold;
    this.groundSteeringGain = AUTOPILOT.groundSteeringGain;
    this.groundSteeringDamp = AUTOPILOT.groundSteeringDamp;
  }

  /**
   * @param {number} dt
   * @param {number} yaw — target look yaw (world, rad)
   * @param {number} pitch — target look pitch above the horizon (rad)
   * @param {boolean} gunZoom — RMB-zoomed in the cockpit with the GAU-8 up
   */
  update(dt, yaw, pitch, gunZoom) {
    const aircraft = this.aircraft;
    const out = this.out;

    // Zoom-boost blend: ramps to 1 when RMB-zooming in cockpit mode with the
    // GAU-8 selected, else 0. Lerps roll/pitch gains toward their tighter
    // "gunnery" values so the bore tracks the look-vector aggressively while
    // aiming the gun. RMB still drives FOV/head-shift in other weapon modes;
    // only the control-feel boost is gun-gated.
    const zoomAlpha = 1 - Math.exp(-CAMERA.fovSmoothing * dt);
    this._zoomBoost += ((gunZoom ? 1 : 0) - this._zoomBoost) * zoomAlpha;
    const zb = this._zoomBoost;
    const effRollGain    = this.rollGain    + (this.zoomRollGain    - this.rollGain)    * zb;
    const effRollDamping = this.rollDamping + (this.zoomRollDamping - this.rollDamping) * zb;
    const effPitchGain   = this.pitchGain   + (this.zoomPitchGain   - this.pitchGain)   * zb;
    const effPitchDamping= this.pitchDamping+ (this.zoomPitchDamping- this.pitchDamping)* zb;

    // 1. Target direction from yaw/pitch
    _targetDir.set(
      -Math.sin(yaw) * Math.cos(pitch),
      Math.sin(pitch),
      -Math.cos(yaw) * Math.cos(pitch),
    );

    // 2. Get aircraft vectors
    const fwd = aircraft.forwardVector;
    const right = aircraft.rightVector;
    const up = aircraft.upVector;
    const airspeed = aircraft.airspeed;

    // 3. Heading error — project onto XZ plane (used for ground steering, rudder, low-bank turn logic)
    _fwdXZ.set(fwd.x, 0, fwd.z).normalize();
    _targetXZ.set(_targetDir.x, 0, _targetDir.z).normalize();
    _cross.crossVectors(_fwdXZ, _targetXZ);
    const dot = _fwdXZ.dot(_targetXZ);
    const headingError = Math.atan2(_cross.y, dot);
    const absHeadingError = Math.abs(headingError);

    // 4. Off-boresight angle — how far the look vector is off the nose.
    //    Used to set both the magnitude of the bank command (input curve) and
    //    to clip aggression at the speed-dependent maxBank.
    const fwdDotTarget = clamp(fwd.dot(_targetDir), -1, 1);
    const offBore = Math.acos(fwdDotTarget);

    // Current bank (signed; positive = left bank in this codebase).
    const currentBank = Math.atan2(right.y, up.y);

    // Body-frame angular velocity (for PD rate damping). aircraft.angularVelocity
    // is world-frame; transform to body local once and reuse for roll & pitch.
    _invQuat.copy(aircraft.quaternion).invert();
    _omegaBody.copy(aircraft.angularVelocity).applyQuaternion(_invQuat);
    // Body conventions in this codebase: +Z body = backward, so a positive
    // rotation around +Z is "roll left" (matches the bankTarget sign convention).
    // +X body = right, so a positive rotation around +X is "nose up".
    const rollRate = _omegaBody.z;
    const pitchRate = _omegaBody.x;

    // 5. Pitch error — body-X projection of (forward × target).
    //    (forward × target) is the rotation axis that swings the nose toward
    //    target; the elevator commands rotation around body-X (right). The dot
    //    product is therefore exactly the elevator demand, expressed as
    //    sin(elev_angle) ∈ [-1, 1]. Properties:
    //      - Smooth across all orientations; no atan2 wraparound, no wobble.
    //      - Zero for horizon-scan at wings level (target lateral ⇒ no climb).
    //      - Saturates to +1 (full pull) at 90° bank with target on the lift
    //        vector — exactly the "pull through the turn" command we want.
    //      - Naturally pushes for shallow descents (small downward target,
    //        wings level), so glide-path tracking on landing still works.
    //      - Drives split-S correctly: once rolled past 90°, the projection
    //        flips sign and pulling rotates the nose downward in world frame.
    _cross.crossVectors(fwd, _targetDir);
    const pitchError = right.dot(_cross);
    // Body-yaw error — body-Y projection of (forward × target). This is the
    // rotation around body-up needed to swing the bore onto the look-vector.
    // Used in zoom (gunnery) mode for active rudder pointing.
    const bodyYawError = up.dot(_cross);

    // 6. Bank target — "tilt the lift vector from world-up toward the target,
    //    in proportion to how far off-boresight the target is."
    //
    //    Geometry:  desiredUp = worldUpPerp + aggression · perpTarget
    //    where perpTarget = target component perpendicular to forward (unit vec),
    //    and aggression = tan(min(offBore, maxBank)).
    //
    //    The crucial change from earlier iterations: aggression is no longer a
    //    constant tan(maxBank). It scales with offBore so small look offsets
    //    produce small banks (linear: bankTarget ≈ offBore for small offBore),
    //    saturating at maxBank for large offsets (sustainable-G coordinated turn).
    //    For below-horizon targets, perpTarget.y is negative — desiredUp.y can
    //    flip negative, automatically commanding bank > 90° (inverted, split-S).
    //
    //    War Thunder–style feel curve (high-speed, maxBank=75°):
    //       5° look →  ~5° bank          (gentle takeoff/cruise correction)
    //      30° look →  ~30° bank         (committed turn)
    //      60° look →  ~60° bank         (hard turn)
    //      75°+ look →  75° bank cap     (max sustainable G)
    //      below-horizon → bank past 90°  (inverted pull / split-S)
    const speedT = clamp((airspeed - this.bankSpeedLow) / (this.bankSpeedHigh - this.bankSpeedLow), 0, 1);
    const maxBank = this.maxBankAngleLow + speedT * (this.maxBankAngleHigh - this.maxBankAngleLow);
    // Sharpening factor: ramps the look→bank curve so moderate look offsets
    // already command near-max bank. 1.0 = linear (offBore°→bankTarget°);
    // higher = punchier (small look offset → bigger bank).
    const sharpenedOffBore = Math.min(offBore * AUTOPILOT.lookSharpness, maxBank);
    const aggression = Math.tan(sharpenedOffBore);

    // perpTarget: component of target perpendicular to forward.
    _perpTarget.copy(_targetDir).addScaledVector(fwd, -fwdDotTarget);
    const perpLen = _perpTarget.length();

    let bankTarget = 0;
    if (perpLen > 1e-3) {
      _perpTarget.divideScalar(perpLen);
      // worldUpPerp: world-up projected perpendicular to forward (the "neutral"
      // body-up direction at zero bank, accounting for current pitch attitude).
      _worldUpPerp.set(0, 1, 0).addScaledVector(fwd, -fwd.y);
      const wupLen = _worldUpPerp.length();
      if (wupLen > 1e-3) {
        _worldUpPerp.divideScalar(wupLen);
        _desiredUp.copy(_worldUpPerp).addScaledVector(_perpTarget, aggression).normalize();
        // Signed bank from worldUpPerp to desiredUp around forward axis.
        _cross2.crossVectors(_worldUpPerp, _desiredUp);
        const sinSigned = _cross2.dot(fwd);
        const cosA = _worldUpPerp.dot(_desiredUp);
        // Negate: convention here is positive currentBank = left bank.
        bankTarget = -Math.atan2(sinSigned, cosA);
      }
    }

    // Wings-level bias: tiny heading error AND target close to forward → relax
    // bank toward zero so cruise / final-approach is rock-stable. (The new input
    // curve already gives small-bank-for-small-offset, but this kills the last
    // bit of mouse-jitter twitch when flying straight.)
    if (absHeadingError < this.wingsLevelThreshold && perpLen < 0.5) {
      const levelBlend = 1 - absHeadingError / this.wingsLevelThreshold;
      bankTarget *= 1 - levelBlend * 0.8;
    }

    // 7. Aileron PD — bank-error proportional + roll-rate damping.
    //    In zoom (gunnery) mode the proportional term fades to zero so the
    //    autopilot stops commanding any specific bank — whatever roll the
    //    aircraft is already in, it holds. Roll-rate damping stays active so
    //    the aircraft doesn't drift in roll while elevator + rudder swing the
    //    bore onto the cursor (whichever axis has authority at this bank).
    const rollProp = effRollGain * (1 - zb);
    const ailerons = clamp(
      (bankTarget - currentBank) * rollProp - rollRate * effRollDamping,
      -1, 1,
    );

    // 8. Elevator PD with coordinated-turn feed-forward.
    //
    //    In a steady level coordinated turn at bank β, the body sees a pitch rate
    //    of (g · sin²β) / (V · cos β) — the world yaw rate Ω = g·tan(β)/V projected
    //    onto body-X via right.y = sin(β). Without the feed-forward, the damping
    //    term `−pitchRate · pitchDamping` treats this as overshoot and pushes the
    //    nose down, dropping the lift vector and inducing the pitch up/down cycle
    //    you get in horizon-scan turns ("bad at coordinating").
    //
    //    With the feed-forward, damping only opposes deviations from the
    //    expected steady-state arc, so the pitch loop and roll loop stop fighting.
    //    Gated to upright flight (cos β > 0.1) — inverted maneuvers get plain
    //    rate damping, which is what we want there.
    const cosBank = Math.cos(currentBank);
    let coordPitchRate = 0;
    if (cosBank > 0.1) {
      const sinBank = Math.sin(currentBank);
      const safeV = Math.max(airspeed, 30);
      coordPitchRate = (sinBank * sinBank) * ENV.gravity / (safeV * cosBank);
    }
    let pitchCommand = pitchError * effPitchGain - (pitchRate - coordPitchRate) * effPitchDamping;

    // AoA protection (positive-G): reduce pull as AoA approaches stall.
    const alpha = aircraft.alpha || 0;
    const stallAngle = aircraft.stallAngle || (15 * DEG2RAD);
    const aoaRatio = Math.abs(alpha) / stallAngle;
    if (aoaRatio > this.aoaProtectionStart && pitchCommand > 0) {
      const protection = Math.max(0, 1 - (aoaRatio - this.aoaProtectionStart) / (1 - this.aoaProtectionStart));
      pitchCommand *= protection;
    }

    const elevator = clamp(pitchCommand, this.negativeGLimit, 1);

    // 9. Apply — two independent concerns:
    //    a) Nosewheel steering vs aerodynamic rudder (speed-based blend)
    //    b) Rudder-primary vs bank-to-turn steering strategy (ground contact)

    // Nosewheel blend: only controls low-speed nosewheel PD steering vs aero rudder
    const nosewheelBlend = aircraft.onGround
      ? 1 - smoothstep((airspeed - GROUND.vGround) / (GROUND.vRotate - GROUND.vGround))
      : 0;

    // On ground: strong rudder, wings level. In flight: bank-to-turn coordination
    // rudder (light) blended with zoom-mode body-yaw pointing rudder (heavy PD)
    // by zoomBoost — gives FPS-cursor feel when zoomed for gunnery.
    const onGround = aircraft.onGround;
    const bodyYawRate = _omegaBody.y;
    const effectiveRudderGain = onGround ? this.rudderGainGround : this.rudderGain;
    const coordRudder = clamp(headingError * effectiveRudderGain, -1, 1);
    const zoomRudder = clamp(bodyYawError * this.zoomYawGain - bodyYawRate * this.zoomYawDamping, -1, 1);
    const rudder = onGround ? coordRudder : (coordRudder * (1 - zb) + zoomRudder * zb);

    if (nosewheelBlend > 0) {
      // Low speed on ground: blend nosewheel PD steering with aero rudder
      const worldYawRate = aircraft.angularVelocity.y;
      const gRudder = Math.max(-1, Math.min(1,
        headingError * this.groundSteeringGain - worldYawRate * this.groundSteeringDamp));
      out.rudder = gRudder * nosewheelBlend + rudder * (1 - nosewheelBlend);
    } else {
      out.rudder = rudder;
    }

    // On ground: suppress bank to keep wings level; use rudder for all yaw control
    out.elevator = elevator;
    out.ailerons = onGround ? 0 : ailerons;

    // Parked on the runway, mouse-look still generates non-zero pitch/heading
    // errors which leak into elevator/rudder. The surface animator reads those
    // and the surfaces twitch; spring-oscillation sub-velocity also lets the
    // tiny rudder command leak past steerRampUp and slowly yaw the airframe.
    // Fade all autopilot output to zero when stopped on the ground.
    if (onGround) {
      const restFade = smoothstep((airspeed - 0.3) / 0.7);
      out.elevator *= restFade;
      out.ailerons *= restFade;
      out.rudder *= restFade;
    }
    return out;
  }
}
